/**
 * SuperBilling — `/super/billing` — operations cockpit for the SaaS:
 *   • Every tenant in one row: plan, status, expires_at, wallet balance
 *   • Inline: Extend subscription, Suspend/Activate, top-up wallet, raise invoice
 *   • Tabs: Subscriptions · Invoices · Wallets
 *
 * Designed for a single-page-of-action super admin — every primary verb is
 * one click away.
 */
import { useEffect, useState } from "react";
import { Plus, Loader2, IndianRupee, Calendar, Pause, Play, FileText, Wallet, Download } from "lucide-react";
import { api, inr2 } from "../../lib/api";
import { toast } from "sonner";
import { Field, Modal } from "../admin/AdminRestaurants";
import TenantSearch, { useTenantSearch } from "../../components/TenantSearch";

const STATUS_PILL = {
    TRIAL: "bg-amber-100 text-amber-700",
    ACTIVE: "bg-emerald-100 text-emerald-700",
    GRACE: "bg-orange-100 text-orange-700",
    SUSPENDED: "bg-red-100 text-red-700",
    CANCELLED: "bg-muted text-muted-foreground",
};

export default function SuperBilling() {
    const [tab, setTab] = useState("subs");
    return (
        <div data-testid="super-billing">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Billing</h1>
                    <p className="text-sm text-muted-foreground mt-1">Subscriptions · Invoices · Wallets</p>
                </div>
                <div className="inline-flex bg-muted rounded-xl p-1 text-xs font-bold">
                    {[
                        ["subs", "Subscriptions"],
                        ["invoices", "Invoices"],
                        ["wallets", "Wallets"],
                    ].map(([id, label]) => (
                        <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)}
                            className={`px-4 py-2 rounded-lg ${tab === id ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="mt-6">
                {tab === "subs" && <SubscriptionsTab />}
                {tab === "invoices" && <InvoicesTab />}
                {tab === "wallets" && <WalletsTab />}
            </div>
        </div>
    );
}

function SubscriptionsTab() {
    const [rows, setRows] = useState([]);
    const [plans, setPlans] = useState([]);
    const [extendFor, setExtendFor] = useState(null);
    const [days, setDays] = useState(30);
    const [planFor, setPlanFor] = useState(null);
    const [superCycle, setSuperCycle] = useState("MONTHLY");
    const { query, setQuery, filter } = useTenantSearch();
    const visible = filter(rows);

    const load = async () => {
        const [s, p] = await Promise.all([
            api.get("/super/subscriptions"),
            api.get("/super/plans"),
        ]);
        setRows(s.data); setPlans(p.data);
    };
    useEffect(() => { load(); }, []);

    const extend = async () => {
        try {
            await api.patch(`/super/tenants/${extendFor.tenant_id}/subscription`, { extend_days: Number(days) });
            toast.success(`${extendFor.tenant_name}: +${days} days · re-activated`);
            setExtendFor(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const toggleActive = async (r) => {
        const active = r.tenant_status !== "ACTIVE";
        try {
            await api.patch(`/super/tenants/${r.tenant_id}/activate`, { active });
            toast.success(`${r.tenant_name}: ${active ? "Activated" : "Suspended"}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const changePlan = async (planId) => {
        try {
            await api.patch(`/super/tenants/${planFor.tenant_id}/subscription`, { plan_id: planId, billing_cycle: superCycle });
            toast.success("Plan updated");
            setPlanFor(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div>
            <div className="flex items-center justify-end mb-3">
                <TenantSearch value={query} onChange={setQuery} placeholder="Search tenant…" testid="subs-search" />
            </div>
            <div className="bg-card border border-border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-muted/60 text-left">
                    <tr>
                        <Th>Tenant</Th><Th>Plan</Th><Th>Status</Th><Th>Expires</Th>
                        <Th>Wallet</Th><Th>Account</Th><Th className="text-right">Actions</Th>
                    </tr>
                </thead>
                <tbody>
                    {visible.map((r) => {
                        const sub = r.subscription || {};
                        const expires = (sub.expires_at || "").slice(0, 10);
                        return (
                            <tr key={r.tenant_id} className="border-t border-border" data-testid={`sub-row-${r.tenant_slug}`}>
                                <Td>
                                    <p className="font-bold">{r.tenant_name}</p>
                                    <p className="text-xs text-muted-foreground">{r.tenant_slug}.bizil.app</p>
                                </Td>
                                <Td>
                                    <button onClick={() => setPlanFor(r)} className="bg-muted hover:bg-muted/80 px-2 py-1 rounded-lg text-xs font-bold inline-flex items-center gap-1">
                                        {r.plan?.code || "—"} <span className="text-muted-foreground">↕</span>
                                    </button>
                                </Td>
                                <Td><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${STATUS_PILL[sub.status] || "bg-muted"}`}>{sub.status || "—"}</span></Td>
                                <Td className="text-xs">{expires}</Td>
                                <Td className="font-bold">{inr2(r.wallet_balance)}</Td>
                                <Td><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${r.tenant_status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.tenant_status}</span></Td>
                                <Td className="text-right">
                                    <div className="inline-flex items-center gap-1.5">
                                        <button data-testid={`extend-${r.tenant_slug}`} onClick={() => setExtendFor(r)} title="Extend subscription" className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600"><Calendar size={14} /></button>
                                        <button data-testid={`toggle-${r.tenant_slug}`} onClick={() => toggleActive(r)} title={r.tenant_status === "ACTIVE" ? "Suspend" : "Activate"} className={`p-2 rounded-lg ${r.tenant_status === "ACTIVE" ? "text-red-600 hover:bg-red-50" : "text-emerald-600 hover:bg-emerald-50"}`}>
                                            {r.tenant_status === "ACTIVE" ? <Pause size={14} /> : <Play size={14} />}
                                        </button>
                                    </div>
                                </Td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            </div>

            {extendFor && (
                <Modal title={`Extend · ${extendFor.tenant_name}`} onClose={() => setExtendFor(null)}>
                    <Field label="Days to extend">
                        <input data-testid="extend-days-input" type="number" min="1" max="365" className="inp" value={days} onChange={(e) => setDays(e.target.value)} />
                    </Field>
                    <p className="text-xs text-muted-foreground mt-3">Re-activates the account if it&apos;s in GRACE or SUSPENDED.</p>
                    <button data-testid="extend-confirm" onClick={extend} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold">Extend & Activate</button>
                </Modal>
            )}

            {planFor && (
                <Modal title={`Change plan · ${planFor.tenant_name}`} onClose={() => setPlanFor(null)}>
                    <p className="text-xs text-muted-foreground mb-3">Switch the tenant to a different plan and billing cycle. Features and limits update immediately.</p>
                    <div className="inline-flex bg-muted rounded-xl p-1 text-xs font-bold mb-4">
                        {["MONTHLY", "YEARLY"].map((c) => (
                            <button key={c} data-testid={`super-cycle-${c}`} onClick={() => setSuperCycle(c)}
                                className={`px-4 py-2 rounded-lg ${superCycle === c ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
                                {c === "MONTHLY" ? "Monthly" : "Yearly"}
                            </button>
                        ))}
                    </div>
                    <div className="space-y-2">
                        {plans.filter((p) => p.status === "ACTIVE" && (!p.is_custom || p.tenant_id === planFor.tenant_id)).map((p) => {
                            const price = superCycle === "YEARLY" ? p.yearly_price : p.monthly_price;
                            const unit = superCycle === "YEARLY" ? "/yr" : "/mo";
                            return (
                                <button key={p.id} data-testid={`pick-plan-${p.code}`} onClick={() => changePlan(p.id)}
                                    className={`w-full text-left border rounded-xl p-3 ${planFor.plan?.id === p.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}>
                                    <div className="flex justify-between">
                                        <div>
                                            <p className="font-bold flex items-center gap-2">{p.name}{p.is_custom && <span className="text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full px-2 py-0.5">CUSTOM</span>}{p.public_to_tenants === false && <span className="text-[10px] font-bold bg-muted text-muted-foreground rounded-full px-2 py-0.5">PRIVATE</span>}</p>
                                            <p className="text-[11px] text-muted-foreground mt-1">{p.limits?.max_stores} stores · {p.limits?.max_orders_per_month} orders/mo · {Object.entries(p.features || {}).filter(([, v]) => v).length} features</p>
                                        </div>
                                        <p className="font-bold text-primary text-lg">{inr2(price)}<span className="text-xs font-medium text-muted-foreground">{unit}</span></p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </Modal>
            )}
        </div>
    );
}

function InvoicesTab() {
    const [rows, setRows] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [newOpen, setNewOpen] = useState(false);
    const [payFor, setPayFor] = useState(null);
    const [refundFor, setRefundFor] = useState(null);
    const [form, setForm] = useState({ tenant_id: "", description: "Subscription · Monthly", rate: "2999", qty: 1, due_in_days: 7, cgst_percent: 9, sgst_percent: 9, igst_percent: 0 });
    const [pay, setPay] = useState({ amount: "", method: "BANK_TRANSFER", reference: "", note: "" });
    const { query, setQuery, filter } = useTenantSearch();
    const tenantById = Object.fromEntries(tenants.map((t) => [t.tenant_id, t]));
    const visible = filter(rows, (inv) => {
        const t = tenantById[inv.tenant_id];
        return [inv.invoice_number, t?.tenant_name, t?.tenant_slug].filter(Boolean).join(" ");
    });

    const load = async () => {
        const [i, s] = await Promise.all([
            api.get("/super/invoices"),
            api.get("/super/subscriptions"),
        ]);
        setRows(i.data);
        setTenants(s.data);
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        try {
            await api.post("/super/invoices", {
                tenant_id: form.tenant_id,
                line_items: [{ description: form.description, hsn: "998314", qty: Number(form.qty), rate: Number(form.rate) }],
                due_in_days: Number(form.due_in_days),
                cgst_percent: Number(form.cgst_percent),
                sgst_percent: Number(form.sgst_percent),
                igst_percent: Number(form.igst_percent),
            });
            toast.success("Invoice created");
            setNewOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const recordPayment = async () => {
        try {
            await api.post(`/super/invoices/${payFor.id}/payments`, { ...pay, amount: Number(pay.amount) });
            toast.success("Payment recorded");
            setPayFor(null); setPay({ amount: "", method: "BANK_TRANSFER", reference: "", note: "" });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const refund = async () => {
        try {
            await api.post(`/super/invoices/${refundFor.id}/refund`, { amount: Number(pay.amount) || refundFor.amount_paid, note: pay.note });
            toast.success("Refund recorded");
            setRefundFor(null); setPay({ amount: "", method: "BANK_TRANSFER", reference: "", note: "" });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
                <TenantSearch value={query} onChange={setQuery} placeholder="Search invoice or tenant…" testid="invoices-search" />
                <button data-testid="new-invoice-btn" onClick={() => setNewOpen(true)} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> New Invoice</button>
            </div>
            <div className="bg-card border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                    <thead className="bg-muted/60 text-left">
                        <tr><Th>Invoice #</Th><Th>Tenant</Th><Th>Date</Th><Th>Total</Th><Th>Paid</Th><Th>Due</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr>
                    </thead>
                    <tbody>
                        {visible.map((inv) => {
                            const t = tenantById[inv.tenant_id];
                            const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/super/invoices/${inv.id}/pdf`;
                            return (
                                <tr key={inv.id} className="border-t border-border" data-testid={`invoice-row-${inv.invoice_number}`}>
                                    <Td className="font-mono text-xs">{inv.invoice_number}</Td>
                                    <Td>{t?.tenant_name || inv.tenant_id.slice(0, 8)}</Td>
                                    <Td className="text-xs">{(inv.invoice_date || "").slice(0, 10)}</Td>
                                    <Td className="font-bold">{inr2(inv.grand_total)}</Td>
                                    <Td>{inr2(inv.amount_paid)}</Td>
                                    <Td>{inr2(inv.amount_due)}</Td>
                                    <Td><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${inv.status === "PAID" ? "bg-emerald-100 text-emerald-700" : inv.status === "PARTIAL" ? "bg-amber-100 text-amber-700" : inv.status === "REFUNDED" ? "bg-purple-100 text-purple-700" : "bg-muted"}`}>{inv.status}</span></Td>
                                    <Td className="text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid={`pdf-${inv.invoice_number}`} className="p-2 rounded-lg hover:bg-muted text-muted-foreground" title="View PDF"><Download size={14} /></a>
                                            {inv.status !== "PAID" && inv.status !== "REFUNDED" && (
                                                <button onClick={() => { setPayFor(inv); setPay({ amount: String(inv.amount_due), method: "BANK_TRANSFER", reference: "", note: "" }); }} title="Record payment" className="px-2 py-1 rounded-lg bg-emerald-600 text-white text-[11px] font-bold">Mark paid</button>
                                            )}
                                            {inv.amount_paid > 0 && (
                                                <button onClick={() => { setRefundFor(inv); setPay({ amount: String(inv.amount_paid), method: "BANK_TRANSFER", reference: "", note: "Refund" }); }} title="Refund" className="px-2 py-1 rounded-lg bg-orange-600 text-white text-[11px] font-bold">Refund</button>
                                            )}
                                        </div>
                                    </Td>
                                </tr>
                            );
                        })}
                        {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground text-sm">No invoices yet. Create one to bill a tenant.</td></tr>}
                        {rows.length > 0 && visible.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground text-sm">No invoices match &ldquo;{query}&rdquo;.</td></tr>}
                    </tbody>
                </table>
            </div>

            {newOpen && (
                <Modal title="New Invoice" onClose={() => setNewOpen(false)}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field full label="Tenant">
                            <select data-testid="new-inv-tenant" className="inp" value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}>
                                <option value="">— Pick tenant —</option>
                                {tenants.map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</option>)}
                            </select>
                        </Field>
                        <Field full label="Description"><input className="inp" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                        <Field label="Qty"><input type="number" className="inp" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} /></Field>
                        <Field label="Rate (₹)"><input type="number" className="inp" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></Field>
                        <Field label="Due in (days)"><input type="number" className="inp" value={form.due_in_days} onChange={(e) => setForm({ ...form, due_in_days: e.target.value })} /></Field>
                        <Field label="CGST %"><input type="number" className="inp" value={form.cgst_percent} onChange={(e) => setForm({ ...form, cgst_percent: e.target.value })} /></Field>
                        <Field label="SGST %"><input type="number" className="inp" value={form.sgst_percent} onChange={(e) => setForm({ ...form, sgst_percent: e.target.value })} /></Field>
                        <Field label="IGST %"><input type="number" className="inp" value={form.igst_percent} onChange={(e) => setForm({ ...form, igst_percent: e.target.value })} /></Field>
                    </div>
                    <button data-testid="new-inv-save" disabled={!form.tenant_id || !form.rate} onClick={create} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold disabled:opacity-50">Create & Send</button>
                </Modal>
            )}

            {payFor && (
                <Modal title={`Record payment · ${payFor.invoice_number}`} onClose={() => setPayFor(null)}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Amount (₹)"><input data-testid="pay-amount" type="number" className="inp" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></Field>
                        <Field label="Method">
                            <select className="inp" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                                <option>BANK_TRANSFER</option><option>RAZORPAY</option><option>MANUAL</option><option>WALLET</option>
                            </select>
                        </Field>
                        <Field full label="Reference"><input className="inp" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="UTR / TxnId" /></Field>
                        <Field full label="Note"><input className="inp" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} /></Field>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">If amount &lt; balance due, the invoice will be marked PARTIAL.</p>
                    <button data-testid="pay-confirm" onClick={recordPayment} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold">Record payment</button>
                </Modal>
            )}

            {refundFor && (
                <Modal title={`Refund · ${refundFor.invoice_number}`} onClose={() => setRefundFor(null)}>
                    <Field label="Amount to refund (₹)"><input data-testid="refund-amount" type="number" className="inp" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></Field>
                    <Field full label="Note"><input className="inp" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} /></Field>
                    <button data-testid="refund-confirm" onClick={refund} className="mt-4 w-full bg-orange-600 text-white rounded-xl py-3 font-bold">Refund</button>
                </Modal>
            )}
        </div>
    );
}

function WalletsTab() {
    const [rows, setRows] = useState([]);
    const [forTenant, setForTenant] = useState(null);
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [txns, setTxns] = useState([]);
    const { query, setQuery, filter } = useTenantSearch();
    const visible = filter(rows);

    const load = async () => {
        const { data } = await api.get("/super/subscriptions");
        setRows(data);
    };
    useEffect(() => { load(); }, []);

    const openTopup = async (r) => {
        setForTenant(r);
        setAmount(""); setNote("");
        try { const { data } = await api.get(`/super/tenants/${r.tenant_id}/wallet`); setTxns(data.transactions || []); }
        catch { setTxns([]); }
    };

    const topup = async () => {
        try {
            await api.post(`/super/tenants/${forTenant.tenant_id}/wallet/topup`, { amount: Number(amount), note });
            toast.success(`+${inr2(Number(amount))} credited`);
            setForTenant(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div>
            <div className="flex items-center justify-end mb-3">
                <TenantSearch value={query} onChange={setQuery} placeholder="Search tenant…" testid="wallets-search" />
            </div>
            <div className="bg-card border border-border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted/60 text-left">
                    <tr><Th>Tenant</Th><Th>Plan</Th><Th>Balance</Th><Th className="text-right">Action</Th></tr>
                </thead>
                <tbody>
                    {visible.map((r) => (
                        <tr key={r.tenant_id} className="border-t border-border" data-testid={`wallet-row-${r.tenant_slug}`}>
                            <Td><p className="font-bold">{r.tenant_name}</p><p className="text-xs text-muted-foreground">{r.tenant_slug}</p></Td>
                            <Td className="text-xs">{r.plan?.code}</Td>
                            <Td className="font-bold inline-flex items-center gap-1"><IndianRupee size={14} className="text-muted-foreground" />{inr2(r.wallet_balance).replace("₹", "")}</Td>
                            <Td className="text-right">
                                <button data-testid={`topup-${r.tenant_slug}`} onClick={() => openTopup(r)} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1"><Wallet size={13} /> Top-up</button>
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>

            {forTenant && (
                <Modal title={`Top-up wallet · ${forTenant.tenant_name}`} onClose={() => setForTenant(null)}>
                    <Field label="Amount (₹)"><input data-testid="topup-amount" type="number" className="inp" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
                    <Field full label="Note (optional)"><input className="inp" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Welcome credit / refund / adjustment" /></Field>
                    <button data-testid="topup-confirm" disabled={!amount} onClick={topup} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold disabled:opacity-50">Credit ₹{amount || "0"}</button>
                    <div className="mt-5 border-t border-border pt-4">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Recent transactions</p>
                        {txns.length === 0 ? <p className="text-xs text-muted-foreground">No activity yet.</p> : (
                            <ul className="space-y-1 max-h-48 overflow-auto">
                                {txns.slice(0, 10).map((tx) => (
                                    <li key={tx.id} className="flex justify-between text-xs">
                                        <span><span className={tx.amount > 0 ? "text-emerald-700 font-bold" : "text-red-600 font-bold"}>{tx.amount > 0 ? "+" : ""}{inr2(tx.amount)}</span> · {tx.note || tx.type}</span>
                                        <span className="text-muted-foreground">{(tx.created_at || "").slice(0, 10)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-3 text-[11px] uppercase tracking-wider font-bold text-muted-foreground ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
